import { Box, Button, Divider, Grid, IconButton, Link, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CdpLogo from '../../Data/Icons/CdpLogo';
import { appURL } from '../../Data/baseURL';
import { PublicContainer, publicPalette } from '../../Public/PublicTheme';

const FooterLink = ({ to, children }) => (
  <Link
    component={NavLink}
    to={to}
    underline="none"
    sx={{
      width: 'fit-content',
      color: publicPalette.muted,
      fontSize: 15,
      transition: 'color 160ms ease, transform 160ms ease',
      '&:hover': {
        color: publicPalette.text,
        transform: 'translateX(3px)',
      },
    }}
  >
    {children}
  </Link>
);

const Footer = () => {
  const { t } = useTranslation();
  const phone = String(t('base.phone') ?? '');
  const email = String(t('base.email') ?? '');

  return (
    <Box
      component="footer"
      sx={{
        color: publicPalette.text,
        backgroundColor: '#030711',
        borderTop: `1px solid ${publicPalette.border}`,
      }}
    >
      <PublicContainer sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={{ xs: 5, md: 8 }}>
          <Grid item xs={12} md={5}>
            <Stack spacing={2.5} sx={{ maxWidth: 430 }}>
              <Button component={NavLink} to="/" sx={{ width: 'fit-content', minWidth: 0, p: 0 }}>
                <CdpLogo style={{ width: 118, height: 'auto' }} />
              </Button>
              <Typography sx={{ color: publicPalette.muted, lineHeight: 1.75 }}>
                {t('base.footer_short_description')}
              </Typography>
              <Stack direction="row" spacing={1.25}>
                <IconButton
                  component="a"
                  href={`mailto:${email}`}
                  aria-label={email}
                  sx={{
                    color: publicPalette.primary,
                    border: `1px solid ${alpha(publicPalette.primary, 0.28)}`,
                    backgroundColor: alpha(publicPalette.primary, 0.08),
                  }}
                >
                  <EmailRoundedIcon />
                </IconButton>
                <IconButton
                  component="a"
                  href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                  aria-label={phone}
                  sx={{
                    color: publicPalette.accent,
                    border: `1px solid ${alpha(publicPalette.accent, 0.28)}`,
                    backgroundColor: alpha(publicPalette.accent, 0.08),
                  }}
                >
                  <PhoneRoundedIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Stack spacing={1.5}>
              <Typography sx={{ mb: 0.5, fontWeight: 800 }}>{t('general.aplication')}</Typography>
              <FooterLink to="/#features">{t('general.characteristics')}</FooterLink>
              <FooterLink to="/#workflow">{t('base.title_howitworks')}</FooterLink>
              <FooterLink to="/#pricing">{t('general.price')}</FooterLink>
              <FooterLink to={`${appURL}/products`}>{t('general.products')}</FooterLink>
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Stack spacing={1.5}>
              <Typography sx={{ mb: 0.5, fontWeight: 800 }}>{t('general.support')}</Typography>
              <FooterLink to={`${appURL}/faq`}>{t('faq.frequent_questions')}</FooterLink>
              <FooterLink to={`${appURL}/support`}>{t('general.support')}</FooterLink>
              <FooterLink to={`${appURL}/terms-conditions`}>
                {t('terms_and_conditions.terms_and_conditions')}
              </FooterLink>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <Stack spacing={1.5}>
              <Typography sx={{ mb: 0.5, fontWeight: 800 }}>{t('contact.contact')}</Typography>
              <Link href={`mailto:${email}`} underline="none" sx={{ color: publicPalette.muted }}>
                {email}
              </Link>
              <Link href={`tel:${phone.replace(/[^\d+]/g, '')}`} underline="none" sx={{ color: publicPalette.muted }}>
                {phone}
              </Link>
              <Button
                component={NavLink}
                to={`${appURL}/signup`}
                endIcon={<ArrowOutwardRoundedIcon />}
                sx={{
                  width: 'fit-content',
                  mt: 1,
                  px: 0,
                  color: publicPalette.primary,
                  textTransform: 'none',
                  fontWeight: 800,
                }}
              >
                {t('button.signup_for_free')}
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5, borderColor: publicPalette.border }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography sx={{ color: publicPalette.muted, fontSize: 14 }}>
            © {new Date().getFullYear()} CONTDEPLATA.MD. {t('public.footer_rights', 'All rights reserved.')}
          </Typography>
          <Typography sx={{ color: alpha(publicPalette.muted, 0.72), fontSize: 13 }}>
            {t('public.footer_tagline')}
          </Typography>
        </Stack>
      </PublicContainer>
    </Box>
  );
};

export default Footer;
